import React from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import buildApiRequestConfig from '../../api/url-builder';
import routes from '../../api/routes';
import { ReportSnapshot } from '../../util/report-snapshot';
import DocumentLinkSettings from './document-link-settings';

export interface ShareReportProps {
    open: boolean;
    onClose: () => void;
    entityId: number | null;
    defaultTitle: string;
    buildSnapshot: () => ReportSnapshot;
}

// Share button on the report pages (Forecast, Forecast vs Actual). Opens the
// same link settings modal every other Share button uses. Nothing is created
// until the first Save: that freezes a snapshot of what's on screen right now
// (POST /documents/from-webpage - filed under Summaries) and creates the link
// on it with the chosen settings. The modal then stays open on that link so it
// can be copied straight away; later Saves just update it.
const ShareReport: React.FC<ShareReportProps> = ({ open, onClose, entityId, defaultTitle, buildSnapshot }) => {
    const createSnapshotDocument = React.useCallback(async (): Promise<string> => {
        if (!entityId) throw new Error('No company selected');
        const snapshot = buildSnapshot();
        const { url, method } = buildApiRequestConfig(routes.documents.fromWebpage);
        const token = Cookies.get('token');
        const { data } = await axios({
            url,
            method,
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            data: {
                title: defaultTitle,
                webpageUrl: snapshot.reportType,
                snapshotData: snapshot,
                accessType: 'anonymous',
                entityId,
            },
        }).catch((err) => {
            throw new Error(err?.response?.data?.error || 'Failed to create the share');
        });
        if (!data?.document?.slug) throw new Error('Failed to create the share');
        return data.document.slug;
    }, [entityId, defaultTitle, buildSnapshot]);

    return (
        <DocumentLinkSettings
            open={open}
            documentSlug={null}
            documentTitle={defaultTitle}
            entityId={entityId}
            link={null}
            createDocument={createSnapshotDocument}
            keepOpenAfterCreate
            onClose={onClose}
            onSaved={() => { /* stays open on the created link - see keepOpenAfterCreate */ }}
        />
    );
};

export default ShareReport;
